import moment from 'moment';
import { useMemo } from 'react';

export const DATE_RANGE_TYPES = {
  last_month: 'MONTH_1',
  last_6_month: 'MONTH_6',
  last_year: 'YEAR_1',
};

interface ChartDataItem {
  price: number;
  date: string;
  source?: string;
  supplierOfferId?: string;
  supplierOfferUuid?: string;
  purchaseRequestId?: string;
  [key: string]: any;
}

interface ProcessedChartData {
  data: any[];
  totalDays?: number;
}

export const useProcessedChartData = (chartData: ChartDataItem[], selectedDateRange: string): ProcessedChartData => {
  return useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { data: [] };
    }

    let mockDataPointId = 1;
    const now = moment();

    // Filter data based on selected date range
    let startDate: moment.Moment;
    let filterStartDate: moment.Moment;

    if (selectedDateRange === DATE_RANGE_TYPES.last_month) {
      startDate = now.clone().subtract(1, 'month').startOf('day');
      filterStartDate = startDate.clone();
    } else if (selectedDateRange === DATE_RANGE_TYPES.last_6_month) {
      // For 6 months: go back exactly 6 months from start of month
      // E.g., if current is Aug 2024, show Feb, Mar, Apr, May, Jun, Jul, Aug
      startDate = now.clone().subtract(6, 'months').startOf('month');
      filterStartDate = startDate.clone();
    } else {
      // For 1 year: go back exactly 12 months from start of month
      startDate = now.clone().subtract(12, 'months').startOf('month');
      filterStartDate = startDate.clone();
    }

    const filteredData = chartData.filter((item) => {
      const itemDate = moment(item.date);
      return itemDate.isSameOrAfter(filterStartDate) && itemDate.isSameOrBefore(now);
    });

    let processedData: any[] = [];

    if (selectedDateRange === DATE_RANGE_TYPES.last_month) {
      // Process data for last month view
      const daysInRange = now.diff(startDate, 'days');
      // Sort data by date first to ensure consistent ordering
      const sortedData = filteredData.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

      processedData = sortedData.map((item: any, _index: number) => {
        const itemDate = moment(item.date);
        const dayPosition = itemDate.diff(startDate, 'days');

        // Improved time-based micro-positioning for same-day data points
        const timeOfDay = (itemDate.hour() * 60 + itemDate.minute() + itemDate.second() / 60) / (24 * 60);

        // Add extra spacing for multiple items on the same day
        const sameDayItems = sortedData.filter(
          (otherItem) => moment(otherItem.date).format('YYYY-MM-DD') === itemDate.format('YYYY-MM-DD')
        );
        const sameDayIndex = sameDayItems.findIndex((sameDayItem) => sameDayItem.date === item.date);
        const extraSpacing = sameDayItems.length > 1 ? sameDayIndex * 0.1 : 0;

        const precisePosition = dayPosition + timeOfDay + extraSpacing;

        // Ensure all non-zero data points have proper navigation data
        const hasPrice = item.price && item.price > 0;
        const sourceId = item.supplierOfferId || item.sourceId || (hasPrice ? `mock-${mockDataPointId++}` : '');
        const supplierOfferUuid =
          item.supplierOfferUuid ||
          (hasPrice ? `mock-uuid-${itemDate.format('YYYY-MM-DD')}-${Math.random().toString(36).substr(2, 9)}` : '');
        return {
          name: precisePosition,
          price: item.price,
          date: item.date,
          purchaseRequestId: item.purchaseRequestId,
          displayDate: itemDate.format('DD.MM.YYYY HH:mm:ss'),
          fullDate: itemDate.format('DD.MM.YYYY HH:mm:ss'),
          formattedPrice: `€${item.price}`,
          source: item.source || 'SUPPLIER_OFFER',
          sourceId: sourceId,
          supplierOfferUuid: supplierOfferUuid,
          dayLabel: itemDate.format('DD'),
          monthLabel: itemDate.format('MMM'),
          displayLabel: itemDate.format('MMM-DD'),
        };
      });

      // Add zero-value data point at the start if no data exists there
      const hasDataAtStart = processedData.some((item: any) => item.name >= 0 && item.name < 1);
      if (!hasDataAtStart) {
        const firstDayDate = startDate.clone();
        processedData.unshift({
          name: 0,
          price: 0,
          date: firstDayDate.toISOString(),
          displayDate: firstDayDate.format('DD.MM.YYYY HH:mm:ss'),
          fullDate: firstDayDate.format('DD.MM.YYYY HH:mm:ss'),
          formattedPrice: '€0',
          source: 'No Data',
          sourceId: '',
          purchaseRequestId: '',
          supplierOfferUuid: '',
          dayLabel: firstDayDate.format('DD'),
          monthLabel: firstDayDate.format('MMM'),
          displayLabel: firstDayDate.format('MMM-DD'),
        });
      }

      processedData.sort((a: any, b: any) => a.name - b.name);
      return { data: processedData, totalDays: daysInRange };
    } else {
      // Process data for 6 months or 1 year view
      const baseMonth = startDate.clone().startOf('month');
      const currentDay = now.date();

      // Sort data by date first to ensure consistent ordering
      const sortedData = filteredData.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

      processedData = sortedData.map((item: any, _index: number) => {
        const itemDate = moment(item.date);
        const itemMonth = itemDate.clone().startOf('month');
        const monthPosition = itemMonth.diff(baseMonth, 'months');

        // Improved precise positioning within the month for same-month data points
        const dayWithinMonth = itemDate.date();
        const daysInMonth = itemDate.daysInMonth();
        const dayFraction = (dayWithinMonth - 1) / daysInMonth;
        const timeOfDay = (itemDate.hour() * 60 + itemDate.minute() + itemDate.second() / 60) / (24 * 60);
        const timeFractionWithinMonth = timeOfDay / daysInMonth;

        // Add extra spacing for multiple items on the same day within the same month
        const sameDayItems = sortedData.filter((otherItem) => {
          const otherDate = moment(otherItem.date);
          return otherDate.format('YYYY-MM-DD') === itemDate.format('YYYY-MM-DD');
        });
        const sameDayIndex = sameDayItems.findIndex((sameDayItem) => sameDayItem.date === item.date);
        const extraSpacing = sameDayItems.length > 1 ? sameDayIndex * 0.01 : 0; // Smaller spacing for month view

        const exactPosition = monthPosition + dayFraction + timeFractionWithinMonth + extraSpacing;

        // Ensure all non-zero data points have proper navigation data
        const hasPrice = item.price && item.price > 0;
        const sourceId = item.supplierOfferId || item.sourceId || (hasPrice ? `mock-${mockDataPointId++}` : '');
        const supplierOfferUuid =
          item.supplierOfferUuid ||
          (hasPrice ? `mock-uuid-${itemDate.format('YYYY-MM-DD')}-${Math.random().toString(36).substr(2, 9)}` : '');
        return {
          name: exactPosition,
          price: item.price,
          date: item.date,
          purchaseRequestId: item.purchaseRequestId,
          displayDate: itemDate.format('DD.MM.YYYY HH:mm:ss'),
          fullDate: itemDate.format('DD.MM.YYYY HH:mm:ss'),
          formattedPrice: `€${item.price}`,
          source: item.source || 'SUPPLIER_OFFER',
          sourceId: sourceId,
          supplierOfferUuid: supplierOfferUuid,
          dayLabel: itemDate.format('DD'),
          monthLabel: itemDate.format('MMM'),
          displayLabel: itemDate.format('MMM'),
        };
      });

      // Add zero-value data point at the start if no data exists there
      const hasDataAtStart = processedData.some((item: any) => item.name >= 0 && item.name < 1);
      if (!hasDataAtStart) {
        const firstMonthDate = startDate
          .clone()
          .startOf('month')
          .date(Math.min(currentDay, startDate.clone().startOf('month').daysInMonth()));

        processedData.unshift({
          name: 0,
          price: 0,
          date: firstMonthDate.toISOString(),
          displayDate: firstMonthDate.format('DD.MM.YYYY HH:mm:ss'),
          fullDate: firstMonthDate.format('DD.MM.YYYY HH:mm:ss'),
          formattedPrice: '€0',
          source: 'No Data',
          sourceId: '',
          supplierOfferUuid: '',
          purchaseRequestId: '',
          dayLabel: firstMonthDate.format('DD'),
          monthLabel: firstMonthDate.format('MMM'),
          displayLabel: firstMonthDate.format('MMM'),
        });
      }

      processedData.sort((a: any, b: any) => a.name - b.name);
      return { data: processedData };
    }
  }, [chartData, selectedDateRange]);
};
